import React, { forwardRef, useMemo } from 'react';
import PhoneInputWithCountry, { type Value as PhoneValue } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js/max';
import { cn } from '../utils';

import 'react-phone-number-input/style.css';

export interface PhoneInputProps {
  value?: string;
  onChange?: (value?: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Two-letter ISO country code. Used when `value` is empty or doesn't infer a country. */
  defaultCountry?: string;
  containerClassName?: string;
  className?: string;
}

type DsInputComponentProps = Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> & {
  value?: string;
  onChange?: (value?: PhoneValue) => void;
};

const DsInputComponent = forwardRef<HTMLInputElement, DsInputComponentProps>(
  ({ value, onChange, className, ...props }, ref) => (
    <input
      ref={ref}
      value={value || ''}
      onChange={(e) => onChange?.((e.target.value || undefined) as PhoneValue | undefined)}
      type="tel"
      className={cn(
        'w-full bg-zinc-50 border-none rounded-r-2xl text-sm text-zinc-900 placeholder-zinc-400',
        'focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all py-3.5 pr-4 pl-3',
        className
      )}
      {...props}
    />
  )
);
DsInputComponent.displayName = 'DsInputComponent';

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Enter phone number',
  disabled,
  defaultCountry = 'BE',
  containerClassName,
  className,
}) => {
  const effectiveCountry = useMemo(() => {
    if (value && isValidPhoneNumber(value)) {
      return parsePhoneNumber(value)?.country ?? defaultCountry;
    }
    return defaultCountry;
  }, [value, defaultCountry]);

  const handleChange = (v: unknown) => {
    onChange?.((typeof v === 'string' ? v : undefined) as string | undefined);
  };

  return (
    <div className={cn('w-full flex flex-col gap-2', containerClassName)}>
      {label && (
        <label className="block text-xs font-bold text-zinc-900 uppercase tracking-wide pl-1">
          {label}
        </label>
      )}
      <PhoneInputWithCountry
        value={value}
        onChange={handleChange as any}
        defaultCountry={effectiveCountry as Country}
        international
        countryCallingCodeEditable={false}
        disabled={disabled}
        placeholder={placeholder}
        inputComponent={DsInputComponent}
        className={cn(
          'PhoneInput--ds flex rounded-2xl overflow-hidden bg-zinc-50',
          '[&_.PhoneInputCountry]:bg-zinc-50 [&_.PhoneInputCountry]:pl-4 [&_.PhoneInputCountry]:rounded-l-2xl',
          '[&_.PhoneInputCountrySelect]:bg-transparent [&_.PhoneInputCountrySelectArrow]:text-zinc-400',
          className
        )}
      />
    </div>
  );
};
